/**
 * Context-free navigation progress bar: the app's only one, on every route.
 *
 * The SRP/job/best-companies-dimension/locations/salary/salary-role "island" routes deliberately
 * never mount `LayoutDefault` (progressive-takeover architecture: each mounts its own isolated
 * provider tree straight into an empty container instead, see `pages/+onRenderClient.ts`), so a
 * React-context progress bar living in `LayoutDefault` can't cover them.
 *
 * The `vike:pageTransitionStart`/`vike:pageTransitionEnd` events (dispatched on `window` by
 * `+onPageTransitionStart.ts`/`+onPageTransitionEnd.ts`) fire regardless of which React tree, if
 * any, is mounted, and only for client-side navigations: Vike never runs those hooks on
 * hydration. A plain DOM listener therefore shows progress everywhere without touching the
 * fragile island provider-scoping described in the knowledge graph.
 *
 * There used to also be a React `components/NavigationProgress.tsx` inside `LayoutDefault`. It
 * was removed (2026-10-04): it only became visible once `window.__vikeHasNavigated` was already
 * true when its `isInitialLoad` effect ran, which on a normal visit never happened (that flag is
 * set at the END of the first navigation, and the effect never re-ran), so it never showed; and
 * where it could show it drew a second, identical bar on top of this one.
 */

const STUCK_TIMEOUT_MS = 15_000;

let initialized = false;

export function initStandaloneNavigationProgress(): void {
  if (typeof window === 'undefined' || initialized) return;
  initialized = true;

  const bar = document.createElement('div');
  bar.setAttribute('aria-hidden', 'true');
  bar.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'height:3px',
    'width:0%',
    'z-index:2147483000',
    'pointer-events:none',
    'background:var(--jc-cta, #3b5e45)',
    'box-shadow:0 0 15px color-mix(in srgb, var(--jc-cta, #3b5e45) 60%, transparent), 0 0 30px color-mix(in srgb, var(--jc-cta, #3b5e45) 30%, transparent)',
    'transition:width 0.05s linear, opacity 0.2s ease-out',
    'opacity:0',
  ].join(';');

  let progress = 0;
  let tickHandle: ReturnType<typeof setInterval> | null = null;
  let hideTimeout: ReturnType<typeof setTimeout> | null = null;
  let stuckTimeout: ReturnType<typeof setTimeout> | null = null;

  const clearTimers = () => {
    if (tickHandle !== null) {
      clearInterval(tickHandle);
      tickHandle = null;
    }
    if (hideTimeout !== null) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
    if (stuckTimeout !== null) {
      clearTimeout(stuckTimeout);
      stuckTimeout = null;
    }
  };

  const finish = (): void => {
    clearTimers();
    progress = 100;
    bar.style.width = '100%';
    hideTimeout = setTimeout(() => {
      bar.style.opacity = '0';
      hideTimeout = setTimeout(() => {
        bar.style.width = '0%';
      }, 200);
    }, 150);
  };

  const start = (): void => {
    if (!bar.isConnected) document.body.appendChild(bar);
    clearTimers();
    progress = 0;
    bar.style.opacity = '1';
    bar.style.width = '0%';
    tickHandle = setInterval(() => {
      // Same exponential-easing curve as NavigationProgress.tsx: fast start, slow approach to 90%.
      progress = Math.min(progress + (100 - progress) * 0.1, 90);
      bar.style.width = `${progress}%`;
    }, 50);
    // If vike:pageTransitionEnd never fires (a hung/errored client navigation), don't leave the
    // bar stuck mid-way forever — matches NavigationContext's own NAVIGATION_STUCK_TIMEOUT_MS.
    stuckTimeout = setTimeout(finish, STUCK_TIMEOUT_MS);
  };

  window.addEventListener('vike:pageTransitionStart', start);
  window.addEventListener('vike:pageTransitionEnd', finish);
}
